import { DeleteAllData } from '@/apiError/apiDelete';
import { ShowAllData } from '@/apiError/apiShow';
import { ShowDataById } from '@/apiError/apiShowById';
import '@/assets/style/companyList.scss';
import { useSearchReport } from '@/pages/reports/reuse/DynamicReportFunction';
import ListHeaderComponents from '@/pages/reports/reuse/ListHeaderComponents';
import { generateDynamicColumns } from '@/tables/DynamicTableFetchFunction';
import DeleteModalButton from '@/utils/components/DeleteModalButton';
import Loader from '@/utils/components/Loader';
import GlobalFormModal from '@/utils/modal/GlobalFormModal';
import GlobalModal from '@/utils/modal/Modal';
import { globalSearch } from '@/utils/search/Search';
import DynamicTable from '@/utils/table/DynamicTable';
import { EditFilled } from '@ant-design/icons';
import { Card, Col, Form, Row } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import BankReceivesForm from '../receiveFromBank/BankReceivesForm';

/**
 * Payment Voucher List
 * Displays and manages payment voucher with searches , delete and edit functionalities.
 *
 * @component
 * @returns {React.ReactElement} Payment Voucher List interface.
 */

const DealClose = () => {
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
    total: 0,
  });
  const [partyFormModalVisible, setPartyFormModalVisible] = useState(false);
  const [deleteReferenceId, setDeleteReferenceId] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalType, setModalType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchTerm, setSearchTrem] = useState('');
  const [tableData, setTableData] = useState([]);
  const [showPartyList, setShowPartyList] = useState([]);
  const [showDealNumber, setShowDealNumber] = useState([]);
  const [cashBankData, setCashBankData] = useState([]);
  const [referenceId, setReferenceId] = useState(null);
  const [item, setItem] = useState([]);
  const {
    searchReport,
    handlePageChange,
    triggerSearch,
    loading,
    setIsSearchClicked,
  } = useSearchReport({
    setPagination,
    setTableData,
    url: '/loan/deals/close/',
    startDate,
    endDate,
    page: pagination.page,
    pageSize: pagination.pageSize,
  });

  /**
   * Shows delete confirmation modal.
   * @param {string} referenceId-The referenceId of the ledger to delete
   */
  const showCancelModal = (referenceId) => {
    setModalType('delete');
    setIsModalVisible(true);
    setDeleteReferenceId(referenceId);
  };

  /**
   * Hides the modal
   */
  const handleCancel = () => {
    setIsModalVisible(false);
  };

  /**
   *Handles search input change.
 
   * @param {string} value-The search term.
   */
  const handleSearch = (value) => {
    setSearchTrem(value);
  };

  const openModal = () => {
    setPartyFormModalVisible(true);
  };

  const handleEditClick = async (id) => {
    openModal();
    setReferenceId(id);
    fetchPartyList();
    fetchCashBankList();
    const response = await ShowDataById.getDataById(`/loan/deal/close/${id}/`);
    const formData = response.data?.summaryData;
    setPartyFormModalVisible(true);
    form.setFieldsValue({ ...formData, date: dayjs(formData?.date) });
    if (formData?.bankId) getDealNumber(formData?.bankId);
    if (formData?.issueNumber) getAllFieldsData(formData?.issueNumber);
  };

  const filteredTableData = globalSearch(tableData, searchTerm);
  const searchPaymentVoucherList = () => {
    searchReport({
      page: pagination.current,
      pageSize: pagination.pageSize,
    });
  };
  useEffect(() => {
    searchPaymentVoucherList();
  }, [startDate, endDate]);

  const dynamicColumn = generateDynamicColumns(tableData);

  const columns = useMemo(() => {
    const cols = [...dynamicColumn];
    if (tableData?.length)
      cols.push({
        title: 'Action',
        key: 'action',
        align: 'center',
        width: '6%',

        render: (_, record) => (
          <div
            className="ant-action"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div
              className="ant-button-edit"
              onClick={() => handleEditClick(record?.referenceId)}
            >
              <Link size="medium" className="edit">
                <EditFilled />
              </Link>
            </div>
            <DeleteModalButton
              showCancelModal={(id) => showCancelModal(id)}
              data={record?.referenceId}
            />
          </div>
        ),
      });

    return cols;
  }, [tableData]);

  const deleteDealList = async () => {
    if (!deleteReferenceId) return;
    setIsModalVisible(false);
    const url = `/loan/deal/close/${deleteReferenceId}/`;
    await DeleteAllData.deleteData(url);
    triggerSearch();
  };

  const fetchPartyList = async () => {
    const response = await ShowAllData.showDetails(
      '/loan/deals/close/ledgers/',
    );
    setShowPartyList(response.data);
    const res = await ShowAllData.showDetails(
      '/loan/deals/close/latest-transaction-number/',
    );
    form.setFieldValue('transactionNumber', res?.transactionNumber);
  };

  const getDealNumber = async (id) => {
    const response = await ShowAllData.showDetails(
      `/loan/bank/${id}/deal-close-deals/`,
    );
    setShowDealNumber(response.data);
  };
  const fetchCashBankList = async () => {
    const response = await ShowAllData.showDetails(
      '/vouchers/payment-ledgers/',
    );
    setCashBankData(response.data.particularLedger);
  };

  const getAllFieldsData = async (id) => {
    const response = await ShowAllData.showDetails(
      `/loan/interest-receive-deal/${id}/`,
    );
    const allItem = await ShowAllData.showDetails(
      `/loan/interest-receive-item/${id}/`,
    );
    setItem(allItem.data);
    const valueToUse = response?.data;
    form.setFieldsValue({
      ...valueToUse,
      dealDate: dayjs(valueToUse?.dealDate),
    });
  };
  return (
    <div style={{ position: 'relative', top: 0, marginTop: 0, paddingTop: 0 }}>
      <div className="table">
        <Row gutter={[24, 0]}>
          <Col xs={24} xl={24}>
            <Card variant="borderless" className="circlebox tablespace mb-24">
              <ListHeaderComponents
                handleSearch={handleSearch}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                setIsSearchClicked={setIsSearchClicked}
                triggerSearch={triggerSearch}
                createButtonTitle={`Create New Deal Close`}
                openModal={openModal}
                showModal={true}
                fetchPartyList={fetchPartyList}
              />
              <Loader spinning={loading}>
                <DynamicTable
                  data={filteredTableData}
                  columns={columns}
                  loading={loading}
                  pagination={{
                    current: pagination?.current,
                    total: pagination?.total,
                    pageSize: pagination?.pageSize,
                    onChange: handlePageChange,
                  }}
                />
              </Loader>
            </Card>
          </Col>
        </Row>
      </div>
      <GlobalModal
        isVisible={isModalVisible}
        type={modalType}
        onClose={handleCancel}
        onConfirm={deleteDealList}
      />
      <GlobalFormModal
        isVisible={partyFormModalVisible}
        title={referenceId ? 'Update Deal Close' : 'Create Deal Close'}
        onClose={() => {
          setPartyFormModalVisible(false);
          form.resetFields();
          setReferenceId(null);
        }}
        loading={false}
      >
        <BankReceivesForm
          form={form}
          referenceId={referenceId}
          onClose={() => {
            setPartyFormModalVisible(false);
            form.resetFields();
            setReferenceId(null);
          }}
          loading={loading}
          from="dealClose"
          fetchPartyList={fetchPartyList}
          getAllFieldsData={getAllFieldsData}
          getDealNumber={getDealNumber}
          showPartyList={showPartyList}
          showDealNumber={showDealNumber}
          cashBankData={cashBankData}
          fetchCashBankList={fetchCashBankList}
          refreshTable={triggerSearch}
          item={item}
        />
      </GlobalFormModal>
    </div>
  );
};

export default DealClose;
